// src/contexts/AuthContext.js - VERSION MEJORADA CON PERMISOS GRANULARES

import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  getIdToken,
  reload,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase/config';
import { toast } from 'react-toastify';
import { getEmailActionCodeSettings } from '../utils/emailVerification';
import sucursalesService from '../services/sucursales.service';
import { MODULE_KEYS, mergeCompanyModules } from '../config/modulesCatalog';
import {
  ensureDeviceId,
  ensureSessionId,
  rotateSessionId,
  clearSessionId,
  getSessionStartedAtMs
} from '../utils/sessionControl';
import { isWithinVersionReloadGrace, clearVersionReloadMarker } from '../utils/appVersionGuard';
import { mergeUserProfile } from '../utils/userIdentity';
import { getCloudApiBaseUrl } from '../config/cloudApi';
import { matrixAllows } from '../utils/matrixAllows';
import { isSuperAdminEmail } from '../config/superAdmin';

const AuthContext = createContext();
const DEMO_EMAIL_RE = /@nexopos\.demo\.local$/i;
const DEMO_EMAIL_PHONE_RE = /^demo_(\d+)@nexopos\.demo\.local$/i;

const isDemoLicenseDoc = (license) => {
  const billingModel = String(license?.billingModel || '').trim().toLowerCase();
  return license?.demo === true || billingModel.startsWith('demo');
};

/**
 * Admin SDK repara usuariosOrg + claims. El cliente no puede collectionGroup.
 * @returns {Promise<object|null>}
 */
async function fetchSessionContext(firebaseUser) {
  if (!firebaseUser) return null;
  try {
    const token = await firebaseUser.getIdToken(true);
    const uid = firebaseUser.uid || null;
    const deviceId = ensureDeviceId();
    const sessionId = ensureSessionId(uid);
    const sessionStartedAtMs = getSessionStartedAtMs(uid);
    const response = await fetch(`${getCloudApiBaseUrl()}/auth/session-context`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        ...(deviceId ? { 'x-nexo-device-id': deviceId } : {}),
        ...(sessionId ? { 'x-nexo-session-id': sessionId } : {}),
        ...(sessionStartedAtMs ? { 'x-nexo-session-started-at': String(sessionStartedAtMs) } : {})
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      console.warn(
        '[AUTH] session-context HTTP',
        response.status,
        payload?.code || '',
        payload?.message || ''
      );
      return null;
    }
    // Claims pueden haberse actualizado en el servidor
    await firebaseUser.getIdToken(true);
    return payload?.data || null;
  } catch (e) {
    console.warn('[AUTH] session-context:', e?.message || e);
    return null;
  }
}

function getSessionPermissions(sessionCtx, customClaims = {}) {
  return sessionCtx?.permisos || sessionCtx?.permissions || customClaims.permisos || {};
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const uoUnsubRef = useRef(null);
  
  // Estado para sucursal seleccionada
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [sucursalesDisponibles, setSucursalesDisponibles] = useState([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  // NUEVO: Estado para permisos efectivos
  const [permisosEfectivos, setPermisosEfectivos] = useState({});
  /** Módulos de licencia normalizados (cada clave del catálogo existe; solo `false` desactiva). */
  const [companyModules, setCompanyModules] = useState(() => mergeCompanyModules({}));
  const currentUserSucursalesKey = JSON.stringify(currentUser?.sucursales || []);
  const authUidRef = useRef(null);
  const storageGet = (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };
  const storageSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* storage no disponible */
    }
  };
  const storageRemove = (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage no disponible */
    }
  };

  const resolvePreferredOrgId = useCallback(async (uid, candidateOrgId, email) => {
    try {
      if (!uid) return candidateOrgId || null;
      if (DEMO_EMAIL_RE.test(String(email || '').trim())) return candidateOrgId || null;

      // Fuente primaria: usuariosOrg/{uid} (invitados creados por admin).
      // collectionGroup(usuarios) suele fallar por rules y dejaba orgId=null → "crear empresa".
      try {
        const uoSnap = await getDoc(doc(db, 'usuariosOrg', uid));
        if (uoSnap.exists()) {
          const uo = uoSnap.data() || {};
          const fromUo = String(uo.orgId || uo.companyId || '').trim();
          if (fromUo) return fromUo;
        }
      } catch (uoErr) {
        console.warn('[AUTH] resolvePreferredOrgId usuariosOrg:', uoErr?.message || uoErr);
      }

      if (candidateOrgId) return candidateOrgId;

      const q = query(collectionGroup(db, 'usuarios'), where('uid', '==', uid));
      const snap = await getDocs(q);
      const orgIds = Array.from(
        new Set(
          snap.docs
            .map((d) => String(d.ref.path || '').split('/'))
            .filter((parts) => parts.length >= 2 && parts[0] === 'companies')
            .map((parts) => parts[1])
            .filter(Boolean)
        )
      );
      if (!orgIds.length) return null;

      const scored = await Promise.all(
        orgIds.map(async (orgIdItem) => {
          try {
            const licenseSnap = await getDoc(doc(db, 'companies', orgIdItem, 'config', 'license'));
            const license = licenseSnap.exists() ? licenseSnap.data() || {} : {};
            return { orgId: orgIdItem, demo: isDemoLicenseDoc(license) };
          } catch {
            return { orgId: orgIdItem, demo: false };
          }
        })
      );
      const nonDemo = scored.find((s) => s.demo === false)?.orgId;
      return nonDemo || scored[0]?.orgId || null;
    } catch (e) {
      console.warn('[AUTH] resolvePreferredOrgId:', e?.message || e);
      return candidateOrgId || null;
    }
  }, []);

  const inferDemoPhoneFromEmail = useCallback((email) => {
    const match = String(email || '').trim().toLowerCase().match(DEMO_EMAIL_PHONE_RE);
    const digits = match?.[1] || '';
    if (digits.length < 10 || digits.length > 15) return '';
    return digits;
  }, []);

  const ensureDemoTenantForUser = useCallback(async (firebaseUser, currentOrgId) => {
    if (!firebaseUser) return currentOrgId || null;
    const email = String(firebaseUser.email || '').trim().toLowerCase();
    if (!DEMO_EMAIL_RE.test(email)) return currentOrgId || null;
    if (currentOrgId) return currentOrgId;
    try {
      const callable = httpsCallable(functions, 'ensureDemoTenantAccess');
      const demoPhone = inferDemoPhoneFromEmail(email);
      const result = await callable({ demoPhone });
      const linkedOrgId = result?.data?.orgId || null;
      if (linkedOrgId) {
        storageSet('orgId', linkedOrgId);
        storageSet('companyId', linkedOrgId);
      }
      return linkedOrgId || currentOrgId || null;
    } catch (e) {
      console.warn('[AUTH] ensureDemoTenantForUser:', e?.message || e);
      return currentOrgId || null;
    }
  }, [inferDemoPhoneFromEmail]);

  const ensurePreferredActiveTenant = useCallback(async (preferredOrgId) => {
    if (!preferredOrgId) return;
    try {
      const callable = httpsCallable(functions, 'setActiveTenant');
      await callable({ orgId: preferredOrgId });
    } catch (e) {
      console.warn('[AUTH] No se pudo activar org preferida automáticamente:', e?.message || e);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        ensureDeviceId();
        const prevUid = authUidRef.current;
        if (prevUid && prevUid !== firebaseUser.uid) {
          rotateSessionId(firebaseUser.uid);
        } else {
          ensureSessionId(firebaseUser.uid);
        }
        authUidRef.current = firebaseUser.uid;
        try {
          // Obtener custom claims (roles)
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          const customClaims = tokenResult.claims;
          let resolvedOrgId = null;

          // Establecer orgId inmediatamente desde claims/localStorage (fallback) para evitar redirección temprana
          try {
            const claimCompanyId = customClaims?.companyId || customClaims?.orgId || null;
            const storedCompanyId = storageGet('companyId') || storageGet('orgId');
            resolvedOrgId = claimCompanyId || storedCompanyId || null;
            if (resolvedOrgId) {
              setOrgId(resolvedOrgId);
              storageSet('orgId', resolvedOrgId);
              storageSet('companyId', resolvedOrgId);
              if (claimCompanyId) storageSet('companyId', claimCompanyId);
            }
          } catch {}
          
          resolvedOrgId = await resolvePreferredOrgId(firebaseUser.uid, resolvedOrgId, firebaseUser.email);
          resolvedOrgId = await ensureDemoTenantForUser(firebaseUser, resolvedOrgId);

          // Siempre hidratar matriz efectiva desde /auth/session-context (CAP-POS-002).
          // Evita bypass ADMIN local y overwrite con permisos crudos de Firestore.
          let sessionCtx = await fetchSessionContext(firebaseUser);
          if (sessionCtx?.orgId || sessionCtx?.companyId) {
            resolvedOrgId = sessionCtx.orgId || sessionCtx.companyId || resolvedOrgId;
          }
          if (sessionCtx) {
            const tokenAfter = await firebaseUser.getIdTokenResult(true);
            Object.assign(customClaims, tokenAfter.claims || {});
          }

          await ensurePreferredActiveTenant(resolvedOrgId);
          if (resolvedOrgId) {
            setOrgId(resolvedOrgId);
            storageSet('orgId', resolvedOrgId);
            storageSet('companyId', resolvedOrgId);
          }

          // Obtener datos adicionales del usuario desde Firestore
          let userData = {
            id: firebaseUser.uid,
            email: firebaseUser.email,
            phoneNumber: firebaseUser.phoneNumber || '',
            nombre: customClaims.nombre || firebaseUser.displayName || 'Usuario',
            apellido: customClaims.apellido || '',
            rol: sessionCtx?.rol || customClaims.rol || customClaims.role || 'Usuario',
            rolId: sessionCtx?.rolId || customClaims.rolId || customClaims.rol_id || customClaims.role || '1',
            permisos: getSessionPermissions(sessionCtx, customClaims),
            isOwner: sessionCtx?.isOwner === true,
            activo: customClaims.activo !== false,
            customClaims: customClaims,
            sucursales: sessionCtx?.sucursales || customClaims.sucursales || [],
            orgId: resolvedOrgId,
            companyId: resolvedOrgId,
            emailVerified: firebaseUser.emailVerified
          };

          // Suscribirse en tiempo real a usuariosOrg/{uid} para reflejar orgId al instante
          try {
            if (uoUnsubRef.current) {
              uoUnsubRef.current();
              uoUnsubRef.current = null;
            }
            const uoRef = doc(db, 'usuariosOrg', firebaseUser.uid);
            uoUnsubRef.current = onSnapshot(uoRef, async (snap) => {
              if (snap.exists()) {
                const data = snap.data();
                const newOrgId = data.orgId || data.companyId || resolvedOrgId || null;
                setOrgId(newOrgId);
                setCurrentUser((prevUser) => {
                  if (!prevUser) return prevUser;

                  return {
                    ...prevUser,
                    orgId: newOrgId,
                    companyId: newOrgId,
                    sucursales: Array.isArray(data.sucursales) ? data.sucursales : prevUser.sucursales,
                    roles: Array.isArray(data.roles) ? data.roles : prevUser.roles
                  };
                });
                // Guardar en localStorage para que los servicios puedan acceder
                if (newOrgId) {
                  storageSet('orgId', newOrgId);
                  storageSet('companyId', newOrgId);
                } else {
                  storageRemove('orgId');
                  storageRemove('companyId');
                }
              } else {
                // Un usuario sin empresa debe elegir "Crear empresa" o ser invitado por un administrador.
                console.log('🏢 [AUTH] Usuario sin empresa asociada');
                if (!resolvedOrgId) {
                  setOrgId(null);
                }
              }
            }, (e) => {
              console.warn('⚠️ [AUTH] Snapshot usuariosOrg error:', e.message);
              if (!resolvedOrgId) {
                setOrgId(null);
              }
            });
          } catch (e) {
            console.warn('⚠️ [AUTH] No se pudo suscribir a usuariosOrg:', e.message);
            if (!resolvedOrgId) {
              setOrgId(null);
            }
          }
          
          // MEJORADO: Intentar obtener datos mas completos desde Firestore (solo si tenemos orgId)
          if (resolvedOrgId) {
            try {
              const userDoc = doc(db, 'companies', resolvedOrgId, 'usuarios', firebaseUser.uid);
              const userSnapshot = await getDoc(userDoc);
              
              if (userSnapshot.exists()) {
                const firestoreData = userSnapshot.data();
                console.log('?? [AUTH] Datos de Firestore:', firestoreData);

                // Perfil desde Firestore; la matriz de permisos permanece la de session-context.
                // Overwrite con permisos crudos rompía CxP/compras granulares.
                userData = {
                  ...userData,
                  nombre: firestoreData.nombre || userData.nombre,
                  apellido: firestoreData.apellido || userData.apellido,
                  rol: userData.rol || firestoreData.rol,
                  rolId: userData.rolId || firestoreData.rol_id,
                  permisos: userData.permisos,
                  sucursales: userData.sucursales?.length
                    ? userData.sucursales
                    : (firestoreData.sucursales || userData.sucursales),
                  activo: firestoreData.activo !== false,
                  avatarUrl: firestoreData.avatarUrl || firestoreData.photoURL || firebaseUser.photoURL || null,
                  ultimo_acceso: firestoreData.ultimo_acceso || firestoreData.fechaUltimoAcceso || null,
                  fecha_creacion: firestoreData.fechaCreacion || firestoreData.fecha_creacion || null,
                  displayName: firebaseUser.displayName || `${firestoreData.nombre || ''} ${firestoreData.apellido || ''}`.trim() || null
                };
              }
            } catch (firestoreError) {
              console.warn('?? [AUTH] No se pudieron obtener datos de Firestore:', firestoreError.message);
            }
          } else {
            console.log('?? [AUTH] No hay orgId disponible, saltando consulta a Firestore');
          }
          
          console.log('? [AUTH] Usuario autenticado:', {
            id: userData.id,
            email: userData.email,
            nombre: userData.nombre,
            rol: userData.rol,
            permisosCount: Object.keys(userData.permisos).length,
            orgId: resolvedOrgId
          });
          
          setCurrentUser(userData);
          setIsAuthenticated(true);
          
          // Calcular permisos efectivos
          await calcularPermisosEfectivos(userData, resolvedOrgId);
          
          // Cargar sucursales disponibles para el usuario
          console.log('🏢 [AUTH] Llamando a cargarSucursalesUsuario...');
          await cargarSucursalesUsuario(userData);
          console.log('🏢 [AUTH] cargarSucursalesUsuario completado');
          
        } catch (error) {
          console.error('? [AUTH] Error obteniendo datos de usuario:', error);
          setCurrentUser(null);
          setIsAuthenticated(false);
          setPermisosEfectivos({});
          setCompanyModules(mergeCompanyModules({}));
          setOrgId(null);
        }
      } else {
        const signedOutUid = authUidRef.current;
        setCurrentUser(null);
        setIsAuthenticated(false);
        setSucursalSeleccionada(null);
        setSucursalesDisponibles([]);
        setPermisosEfectivos({});
        setCompanyModules(mergeCompanyModules({}));
        setOrgId(null);
        authUidRef.current = null;
        storageRemove('orgId');
        storageRemove('sucursalSeleccionada');
        clearSessionId(signedOutUid);
        if (uoUnsubRef.current) {
          uoUnsubRef.current();
          uoUnsubRef.current = null;
        }
      }
      setLoading(false);
    });

    return () => {
      if (uoUnsubRef.current) {
        uoUnsubRef.current();
        uoUnsubRef.current = null;
      }
      unsubscribe();
    };
  }, [ensureDemoTenantForUser, ensurePreferredActiveTenant, resolvePreferredOrgId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (orgId) {
        storageSet('orgId', orgId);
        storageSet('companyId', orgId);
      } else {
        storageRemove('orgId');
        storageRemove('companyId');
      }
    } catch (storageError) {
      console.warn('[AUTH] No se pudo sincronizar orgId en localStorage:', storageError);
    }
  }, [orgId]);

  // Efecto para recargar sucursales cuando cambie el orgId
  useEffect(() => {
    if (currentUser && orgId && isAuthenticated) {
      console.log('[AUTH] orgId cambió, recargando sucursales…', { orgId, userId: currentUser.id });
      cargarSucursalesUsuario(currentUser);
    }
  }, [orgId, currentUser?.id, currentUser?.rol, currentUser?.rolId, currentUserSucursalesKey, isAuthenticated]);

  /**
   * Calcula los permisos efectivos del usuario.
   * SSOT: matriz de /auth/session-context (ya resuelta en backend).
   * Solo aplica overlay de módulos de licencia. Sin plantillas ADMIN locales.
   * @param {Object} usuario - Datos del usuario
   * @param {string} orgId - ID de la empresa
   */
  const calcularPermisosEfectivos = async (usuario, orgId) => {
    try {
      console.log('🔐 [AUTH] Calculando permisos efectivos para usuario:', {
        id: usuario?.id,
        email: usuario?.email,
        rol: usuario?.rol,
        rolId: usuario?.rolId,
        isOwner: usuario?.isOwner === true
      });

      let modulosRaw = {};
      if (orgId) {
        try {
          console.log('🔐 [AUTH] Consultando configuración de módulos para orgId:', orgId);
          const modulesRef = doc(db, 'companies', orgId, 'config', 'modules');
          const modulesSnapshot = await getDoc(modulesRef);

          if (modulesSnapshot.exists()) {
            modulosRaw = modulesSnapshot.data();
            console.log('🔐 [AUTH] Módulos en Firestore:', modulosRaw);
          } else {
            console.log('🔐 [AUTH] Sin doc de módulos: se asume todo habilitado (compatibilidad)');
          }
        } catch (error) {
          console.warn('🔐 [AUTH] Error consultando configuración de módulos:', error.message);
        }
      }

      const mergedModules = mergeCompanyModules(modulosRaw);
      setCompanyModules(mergedModules);

      // Matriz efectiva proveniente de session-context (o claims). Sin inventar rol=all.
      const fromSession =
        usuario?.permisos && typeof usuario.permisos === 'object' ? usuario.permisos : {};
      const permisosFinales = {};
      Object.keys(fromSession).forEach((modulo) => {
        const block = fromSession[modulo];
        if (block && typeof block === 'object' && !Array.isArray(block)) {
          permisosFinales[modulo] = { ...block };
        }
      });

      // Licencia / módulos: clave false anula permisos CRUD del módulo
      MODULE_KEYS.forEach((modulo) => {
        if (mergedModules[modulo] === false) {
          console.log(`🔐 [AUTH] Módulo ${modulo} deshabilitado en configuración, sin permisos`);
          permisosFinales[modulo] = { ver: false, crear: false, editar: false, eliminar: false };
        }
      });

      console.log('🔐 [AUTH] Permisos efectivos calculados:', permisosFinales);
      setPermisosEfectivos(permisosFinales);

    } catch (error) {
      console.error('❌ [AUTH] Error al calcular permisos efectivos:', error);
      setPermisosEfectivos({
        productos: { ver: true, crear: false, editar: false, eliminar: false },
        ventas: { ver: true, crear: true, editar: false, eliminar: false }
      });
      setCompanyModules(mergeCompanyModules({}));
    }
  };

  const syncEffectivePermissions = useCallback(async () => {
    if (!currentUser) return;
    const sessionCtx = await fetchSessionContext(auth.currentUser);
    const permisos = getSessionPermissions(sessionCtx, currentUser.customClaims || {});
    const hydratedUser = {
      ...currentUser,
      ...(sessionCtx?.rol ? { rol: sessionCtx.rol } : {}),
      ...(sessionCtx?.rolId ? { rolId: sessionCtx.rolId } : {}),
      ...(sessionCtx?.sucursales ? { sucursales: sessionCtx.sucursales } : {}),
      isOwner: sessionCtx?.isOwner === true,
      permisos,
    };
    setCurrentUser(hydratedUser);
    await calcularPermisosEfectivos(hydratedUser, orgId || sessionCtx?.orgId || sessionCtx?.companyId);
  }, [currentUser, orgId]);

  /**
   * Cargar sucursales disponibles para el usuario
   */
  const cargarSucursalesUsuario = async (usuario) => {
    try {
      console.log('🏢 [AUTH] INICIANDO cargarSucursalesUsuario para usuario:', {
        id: usuario.id,
        email: usuario.email,
        rol: usuario.rol,
        rolId: usuario.rolId,
        orgId: orgId
      });
      
      setLoadingSucursales(true);
      
      let sucursales = [];
      
      if (usuario.rol === 'Administrador' || usuario.rol === 'admin' || usuario.rol === 'Admin' || usuario.rolId === 'admin') {
        // Administrador puede ver todas las sucursales
        console.log('🏢 [AUTH] Usuario es administrador, obteniendo todas las sucursales activas');
        sucursales = await sucursalesService.obtenerActivas();
      } else if (usuario.sucursales && usuario.sucursales.length > 0) {
        // Usuario normal solo ve sus sucursales asignadas
        console.log('🏢 [AUTH] Usuario tiene sucursales asignadas:', usuario.sucursales);
        sucursales = await sucursalesService.obtenerPorUsuario(usuario.id);
      } else {
        // Fallback: si no tiene asignadas, usar activas para garantizar selección
        console.log('🏢 [AUTH] Usuario sin sucursales asignadas, obteniendo sucursales activas');
        sucursales = await sucursalesService.obtenerActivas();
      }
      
      const esSucursalValida = (sucursal) => (
        sucursal
        && sucursal.activa !== false
        && sucursal.visible !== false
      );
      sucursales = sucursales.filter(esSucursalValida);

      console.log('🏢 [AUTH] Sucursales válidas obtenidas:', sucursales.length, sucursales);
      setSucursalesDisponibles(sucursales);
      
      // Seleccionar la primera sucursal por defecto o la guardada en localStorage
      const sucursalGuardada = storageGet('sucursalSeleccionada');
      console.log('🏢 [AUTH] Sucursal guardada en localStorage:', sucursalGuardada);
      
      if (sucursalGuardada) {
        const sucursal = sucursales.find((s) => s.id === sucursalGuardada && esSucursalValida(s));
        if (sucursal) {
          console.log('🏢 [AUTH] Usando sucursal guardada:', sucursal);
          setSucursalSeleccionada(sucursal);
        } else if (sucursales.length > 0) {
          console.log('🏢 [AUTH] Sucursal guardada inválida o no encontrada, usando primera disponible:', sucursales[0]);
          setSucursalSeleccionada(sucursales[0]);
          storageSet('sucursalSeleccionada', sucursales[0].id);
        } else {
          storageRemove('sucursalSeleccionada');
        }
      } else if (sucursales.length > 0) {
        console.log('🏢 [AUTH] No hay sucursal guardada, usando primera disponible:', sucursales[0]);
        setSucursalSeleccionada(sucursales[0]);
        storageSet('sucursalSeleccionada', sucursales[0].id);
      } else {
        // Si aún no hay, crear una sucursal virtual temporal para evitar crashes
        console.log('🏢 [AUTH] No hay sucursales disponibles, creando fallback temporal');
        const fallback = { id: 'sucursal-principal', nombre: 'Sucursal Principal', tipo: 'principal' };
        setSucursalSeleccionada(fallback);
      }
      
      // DEBUG: Verificar el estado final
      console.log('🏢 [AUTH] Estado final después de selección:', {
        sucursalesDisponibles: sucursales.length,
        sucursalSeleccionada: sucursales.length > 0 ? sucursales[0] : 'ninguna'
      });
      
    } catch (error) {
      console.error('❌ [AUTH] Error al cargar sucursales del usuario:', error);
      toast.error('Error al cargar sucursales');
    } finally {
      setLoadingSucursales(false);
    }
  };

  /**
   * Cambiar sucursal seleccionada
   */
  const cambiarSucursal = (sucursalId) => {
    const sucursal = sucursalesDisponibles.find(
      (s) => s.id === sucursalId && s.activa !== false && s.visible !== false
    );
    if (sucursal) {
      setSucursalSeleccionada(sucursal);
      storageSet('sucursalSeleccionada', sucursalId);
      toast.success(`Cambiado a ${sucursal.nombre}`);
    }
  };

  const login = async (email, password) => {
    try {
      console.log("?? [AUTH] Iniciando login con:", email);
      ensureDeviceId();
      rotateSessionId();
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      rotateSessionId(firebaseUser.uid);
      
      // Obtener token con custom claims
      const tokenResult = await firebaseUser.getIdTokenResult(true);
      const customClaims = tokenResult.claims;
      let resolvedOrgId = customClaims.companyId || customClaims.orgId || storageGet('companyId') || storageGet('orgId') || null;
      resolvedOrgId = await resolvePreferredOrgId(firebaseUser.uid, resolvedOrgId, firebaseUser.email);
      resolvedOrgId = await ensureDemoTenantForUser(firebaseUser, resolvedOrgId);

      // Siempre hidratar vía API en login: claims/usuariosOrg pueden estar desfasados
      // tras alta por admin; session-context repara con Admin SDK.
      let sessionCtx = await fetchSessionContext(firebaseUser);
      if (sessionCtx?.orgId || sessionCtx?.companyId) {
        resolvedOrgId = sessionCtx.orgId || sessionCtx.companyId || resolvedOrgId;
      }
      const tokenAfter = await firebaseUser.getIdTokenResult(true);
      Object.assign(customClaims, tokenAfter.claims || {});
      if (!resolvedOrgId) {
        resolvedOrgId = customClaims.companyId || customClaims.orgId || resolvedOrgId;
      }

      await ensurePreferredActiveTenant(resolvedOrgId);
      if (resolvedOrgId) {
        setOrgId(resolvedOrgId);
        storageSet('orgId', resolvedOrgId);
        storageSet('companyId', resolvedOrgId);
      }
      
      const user = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        phoneNumber: firebaseUser.phoneNumber || '',
        nombre: customClaims.nombre || sessionCtx?.nombre || firebaseUser.displayName || 'Usuario',
        apellido: customClaims.apellido || '',
        rol: sessionCtx?.rol || customClaims.rol || customClaims.role || 'Usuario',
        rolId: sessionCtx?.rolId || customClaims.rolId || customClaims.rol_id || customClaims.role || '1',
        permisos: getSessionPermissions(sessionCtx, customClaims),
        isOwner: sessionCtx?.isOwner === true,
        activo: customClaims.activo !== false,
        customClaims: customClaims,
        sucursales: sessionCtx?.sucursales || customClaims.sucursales || [],
        orgId: resolvedOrgId,
        companyId: resolvedOrgId,
        emailVerified: firebaseUser.emailVerified
      };
      
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      // Calcular permisos efectivos
      await calcularPermisosEfectivos(user, resolvedOrgId);
      
      // Cargar sucursales despues del login
      await cargarSucursalesUsuario(user);

      const isDemoSyntheticUser = DEMO_EMAIL_RE.test(String(firebaseUser?.email || '').trim());
      const hasVerifiedPhone = Boolean(String(firebaseUser?.phoneNumber || '').trim());
      if (!firebaseUser.emailVerified && !isDemoSyntheticUser && !hasVerifiedPhone) {
        toast.warning(
          'Debés verificar tu correo para usar 0E3 POS. Abrí el mensaje «Verificación de correo» desde Firebase/noreply, tocá el enlace y revisá también la carpeta de spam.'
        );
      } else {
        toast.success(`Bienvenido ${user.rol}: ${user.email}`);
      }
      return user;
      
    } catch (error) {
      console.error("? [AUTH] ERROR EN LOGIN:", error);
      
      let message = 'Error al iniciar sesion';
      
      if (error.code === 'auth/user-not-found') {
        message = 'Usuario no encontrado';
      } else if (error.code === 'auth/wrong-password') {
        message = 'Contrasena incorrecta';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Email invalido';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Demasiados intentos fallidos. Intenta mas tarde';
      } else if (error.message) {
        message = error.message;
      }
      
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const uid = auth.currentUser?.uid || authUidRef.current || currentUser?.id || null;
      await signOut(auth);
      setCurrentUser(null);
      setIsAuthenticated(false);
      setSucursalSeleccionada(null);
      setSucursalesDisponibles([]);
      setPermisosEfectivos({});
      setCompanyModules(mergeCompanyModules({}));
      storageRemove('sucursalSeleccionada');
      storageRemove('orgId');
      storageRemove('companyId');
      clearSessionId(uid);
      authUidRef.current = null;
      toast.info('Sesion cerrada correctamente');
    } catch (error) {
      console.error('? [AUTH] Error cerrando sesion:', error);
      toast.error('Error al cerrar sesion');
    }
  };

  const signUp = async (email, password, nombreEmpresa = null, options = {}) => {
    ensureDeviceId();
    rotateSessionId();
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = cred.user;
    rotateSessionId(firebaseUser.uid);
    const skipEmailVerification = options?.skipEmailVerification === true;
    if (!skipEmailVerification) {
      auth.languageCode = 'es';
      await sendEmailVerification(firebaseUser, getEmailActionCodeSettings());
    }
    if (nombreEmpresa && typeof window !== 'undefined') {
      const nombreTrim = String(nombreEmpresa).trim();
      sessionStorage.setItem('pendingEmpresaNombre', nombreTrim);
      try {
        let prev = {};
        try {
          prev = JSON.parse(localStorage.getItem('pendingEmpresaOnboarding') || '{}') || {};
        } catch {
          prev = {};
        }
        const payload = {
          ...prev,
          nombre: nombreTrim,
          email: email || prev.email || '',
          businessType: options?.businessType || prev.businessType || '',
          ts: Date.now()
        };
        localStorage.setItem('pendingEmpresaOnboarding', JSON.stringify(payload));
      } catch {
        // ignore storage errors
      }
    }
    setCurrentUser({
      id: firebaseUser.uid,
      email: firebaseUser.email,
      phoneNumber: firebaseUser.phoneNumber || '',
      nombre: firebaseUser.displayName || 'Usuario',
      rol: 'Usuario',
      rolId: '1',
      permisos: {},
      activo: true,
      sucursales: [],
      orgId: null,
      emailVerified: firebaseUser.emailVerified
    });
    setIsAuthenticated(true);
    if (!skipEmailVerification) {
      toast.success(
        'Te enviamos un correo de verificación en español al mail que ingresaste. Este paso protege tu cuenta y la información de tus clientes antes de habilitar la empresa.'
      , { autoClose: 6500 });
    } else {
      toast.success(
        'Cuenta demo creada. Confirmá tu número de celular para activar la demo y empezar a probar.',
        { autoClose: 5000 }
      );
    }
    return firebaseUser;
  };

  /**
   * Tras verificar correo desde el link: hay que hacer reload del usuario,
   * forzar token nuevo (claim email_verified) y actualizar el estado React;
   * si no, createTenant/login ven token viejo y la UI sigue como no verificado.
   */
  const refreshAuthSession = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return { emailVerified: false };
    }
    try {
      await reload(firebaseUser);
      await getIdToken(firebaseUser, true);
      const tokenResult = await firebaseUser.getIdTokenResult(true);
      const verified = firebaseUser.emailVerified === true;
      const claimCompanyId =
        tokenResult.claims?.companyId ||
        tokenResult.claims?.orgId ||
        null;

      setCurrentUser((prev) => {
        if (!prev) return prev;
        const nextOrg =
          prev.orgId ||
          prev.companyId ||
          claimCompanyId ||
          storageGet('companyId') ||
          storageGet('orgId') ||
          null;
        return mergeUserProfile(
          {
            ...prev,
            emailVerified: verified,
            ...(nextOrg ? { orgId: nextOrg, companyId: nextOrg } : {}),
            displayName: firebaseUser.displayName || prev.displayName,
            avatarUrl: firebaseUser.photoURL || prev.avatarUrl,
            photoURL: firebaseUser.photoURL || prev.photoURL
          },
          {}
        );
      });

      if (claimCompanyId) {
        setOrgId((pid) => pid || claimCompanyId);
        storageSet('orgId', claimCompanyId);
        storageSet('companyId', claimCompanyId);
      }

      return { emailVerified: verified };
    } catch (e) {
      console.warn('[AUTH] refreshAuthSession:', e?.message || e);
      return { emailVerified: firebaseUser.emailVerified === true };
    }
  }, []);

  /** Recarga perfil desde Firestore del tenant activo. */
  const reloadUserProfile = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    const activeOrgId = orgId || storageGet('orgId') || storageGet('companyId');
    if (!firebaseUser || !activeOrgId) return null;

    try {
      await getIdToken(firebaseUser, true);
      const userSnapshot = await getDoc(doc(db, 'companies', activeOrgId, 'usuarios', firebaseUser.uid));
      if (!userSnapshot.exists()) return null;

      const firestoreData = userSnapshot.data();
      let merged = null;
      setCurrentUser((prev) => {
        if (!prev) return prev;
        merged = mergeUserProfile(prev, {
          nombre: firestoreData.nombre || prev.nombre,
          apellido: firestoreData.apellido || prev.apellido,
          email: firestoreData.email || firebaseUser.email || prev.email,
          rol: firestoreData.rol || prev.rol,
          rolId: firestoreData.rol_id || prev.rolId,
          avatarUrl: firestoreData.avatarUrl || firestoreData.photoURL || firebaseUser.photoURL || null,
          ultimo_acceso: firestoreData.ultimo_acceso || firestoreData.fechaUltimoAcceso || prev.ultimo_acceso,
          fecha_creacion: firestoreData.fechaCreacion || firestoreData.fecha_creacion || prev.fecha_creacion,
          displayName: firebaseUser.displayName || `${firestoreData.nombre || ''} ${firestoreData.apellido || ''}`.trim() || prev.displayName
        });
        return merged;
      });
      return merged;
    } catch (e) {
      console.warn('[AUTH] reloadUserProfile:', e?.message || e);
      return null;
    }
  }, [orgId]);

  const updateUserProfile = useCallback((patch = {}) => {
    setCurrentUser((prev) => mergeUserProfile(prev, patch));
  }, []);

  const completeCompanyAfterVerification = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No hay sesión activa');
    }
    const tokenResult = await firebaseUser.getIdTokenResult(true);
    const customClaims = tokenResult.claims;
    let resolvedOrgId =
      customClaims.companyId ||
      customClaims.orgId ||
      storageGet('companyId') ||
      storageGet('orgId') ||
      null;
    const sessionCtx = await fetchSessionContext(firebaseUser);
    if (sessionCtx?.orgId || sessionCtx?.companyId) {
      resolvedOrgId = sessionCtx.orgId || sessionCtx.companyId || resolvedOrgId;
    }
    if (resolvedOrgId) {
      setOrgId(resolvedOrgId);
      storageSet('orgId', resolvedOrgId);
      storageSet('companyId', resolvedOrgId);
    }
    const user = {
      id: firebaseUser.uid,
      email: firebaseUser.email,
      phoneNumber: firebaseUser.phoneNumber || '',
      nombre: customClaims.nombre || sessionCtx?.nombre || firebaseUser.displayName || 'Usuario',
      apellido: customClaims.apellido || '',
      rol: sessionCtx?.rol || customClaims.rol || customClaims.role || 'Usuario',
      rolId: sessionCtx?.rolId || customClaims.rolId || customClaims.rol_id || customClaims.role || '1',
      permisos: getSessionPermissions(sessionCtx, customClaims),
      isOwner: sessionCtx?.isOwner === true,
      activo: customClaims.activo !== false,
      customClaims,
      sucursales: sessionCtx?.sucursales || customClaims.sucursales || [],
      orgId: resolvedOrgId,
      companyId: resolvedOrgId,
      emailVerified: firebaseUser.emailVerified
    };
    setCurrentUser(user);
    await calcularPermisosEfectivos(user, resolvedOrgId);
    await cargarSucursalesUsuario(user);
  };

  // Funcion para obtener token de acceso
  const getAccessToken = async () => {
    if (auth.currentUser) {
      return await getIdToken(auth.currentUser);
    }
    return null;
  };

  /**
   * Verificar permisos vía matriz efectiva (session-context) + matrixAllows.
   * Sin bypass por rol ADMIN. Super-admin email alineado a functions/utils/rbac.userCan.
   * @param {string} modulo - Modulo a verificar
   * @param {string} accion - Accion a verificar
   * @returns {boolean} Tiene permiso
   */
  const hasPermission = (modulo, accion) => {
    try {
      if (companyModules?.[modulo] === false) {
        return false;
      }
      if (isSuperAdminEmail(currentUser?.email)) {
        return true;
      }
      return matrixAllows(permisosEfectivos, modulo, accion) === true;
    } catch (error) {
      console.error('❌ [AUTH] Error al verificar permisos:', error);
      return false;
    }
  };

  /**
   * NUEVO: Verificar si el usuario puede acceder a una sucursal especifica
   * @param {string} sucursalId - ID de la sucursal
   * @returns {boolean} Puede acceder
   */
  const canAccessSucursal = (sucursalId) => {
    try {
      // Administrador puede acceder a todas las sucursales
      if (currentUser?.rol === 'Administrador' || currentUser?.rol === 'admin' || currentUser?.rol === 'Admin') {
        return true;
      }
      
      // Verificar si la sucursal esta en la lista de sucursales disponibles
      return sucursalesDisponibles.some(s => s.id === sucursalId);
      
    } catch (error) {
      console.error('? [AUTH] Error al verificar acceso a sucursal:', error);
      return false;
    }
  };

  /**
   * NUEVO: Obtener informacion completa del usuario para debugging
   */
  const getUserInfo = () => {
    return {
      user: currentUser,
      permissions: permisosEfectivos,
      sucursales: sucursalesDisponibles,
      sucursalActual: sucursalSeleccionada
    };
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    login,
    signUp,
    refreshAuthSession,
    completeCompanyAfterVerification,
    syncEffectivePermissions,
    logout,
    getAccessToken,
    hasPermission,
    sucursalSeleccionada,
    sucursalesDisponibles,
    loadingSucursales,
    cambiarSucursal,
    canAccessSucursal,
    permisosEfectivos,
    companyModules,
    getUserInfo,
    orgId,
    updateUserProfile,
    reloadUserProfile
  };

  // Exportar al objeto global para debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.hasPermission = hasPermission;
      window.authContext = value;
      window.debugAuth = () => {
        console.log('🔍 [AUTH DEBUG] Estado completo:', {
          currentUser,
          orgId,
          sucursalSeleccionada,
          sucursalesDisponibles,
          loadingSucursales,
          permisosEfectivos
        });
      };
      console.log('🔐 [AUTH] Funciones exportadas al objeto global para debugging');
      console.log('🔐 [AUTH] Usa window.debugAuth() para ver el estado completo');
    }
  }, [value]);

  useEffect(() => {
    const onForceLogout = async (event) => {
      if (isWithinVersionReloadGrace()) {
        clearVersionReloadMarker();
        console.info('[AUTH] SESSION_* ignorado durante recarga de versión');
        return;
      }
      const code = String(event?.detail?.code || '');
      const message =
        event?.detail?.message ||
        (code === 'SESSION_REPLACED'
          ? 'Tu sesión fue reemplazada por un inicio más reciente en otro dispositivo.'
          : 'Tu sesión se abrió en otro dispositivo. Volvé a iniciar sesión.');
      const uid = auth.currentUser?.uid || authUidRef.current || currentUser?.id || null;
      try {
        await signOut(auth);
      } catch {}
      setCurrentUser(null);
      setIsAuthenticated(false);
      setSucursalSeleccionada(null);
      setSucursalesDisponibles([]);
      setPermisosEfectivos({});
      setCompanyModules(mergeCompanyModules({}));
      setOrgId(null);
      authUidRef.current = null;
      storageRemove('sucursalSeleccionada');
      storageRemove('orgId');
      storageRemove('companyId');
      clearSessionId(uid);
      toast.warning(message);
    };

    window.addEventListener('auth:force-logout', onForceLogout);
    return () => window.removeEventListener('auth:force-logout', onForceLogout);
  }, []);

  // Siempre montar children: el loader vive en AppContent / ProtectedRoute.
  // Antes `{!loading && children}` dejaba #root vacío en móvil mientras Firebase tardaba → pantalla blanca.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
